import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getToken, logout, getUserId } from "../utils/auth";
import AssignmentTimer from "./AssignmentTimer";
import { clearAssignmentTimer } from "../utils/assignmentTimer";
import swal from "sweetalert";
import "./AssignmentSolvePage.css";

export default function AssignmentSolvePage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const studentId = getUserId();
  const fullscreenExitCount = useRef(0);
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedQuestions, setCompletedQuestions] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  

  /* ---------- ENTER FULLSCREEN ---------- */
  const enterFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      }
    } catch (err) {
      console.warn("Fullscreen blocked", err);
    }
  };

  /* ---------- SUBMIT ASSIGNMENT ---------- */
  const submitAssignment = useCallback(
  async (reason = "manual") => {
    if (submitting || submitted) return;

    setSubmitting(true);

    try {
      console.log("Submitting assignment...");
      console.log("Token:", getToken());
      console.log("Completed:", completedQuestions);

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/assignment-submissions/submit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            assignmentId,
            studentId,
            solvedQuestions: completedQuestions,
            isFinalSubmission: true,
            submitReason: reason
          })
        }
      );

      const data = await res.json();
      console.log("Response:", data);

      if (res.status === 401 || res.status === 403) {
        logout();
        navigate("/login");
        return;
      }

      if (!res.ok) throw new Error("Submit failed");

      clearAssignmentTimer(assignmentId, studentId);
      setSubmitted(true);

      swal({
        title: "Submitted",
        text: "Assignment submitted successfully.",
        icon: "success",
        button: "OK"
      }).then(() => {
        navigate("/assignment-student");
      });

    } catch (err) {
      console.error("Submit error:", err);

      swal({
        title: "Error",
        text: "Submission failed",
        icon: "error",
        button: "OK"
      });
    } finally {
      setSubmitting(false);
    }
  },
  [assignmentId, studentId, completedQuestions, navigate, submitting, submitted]
);
/* ---------- FULLSCREEN AUTO SUBMIT (2-STRIKE RULE) ---------- */
  useEffect(() => {
    enterFullscreen();

    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;

      if (!isNowFullscreen) {
        fullscreenExitCount.current += 1;

        // ✅ First time → just re-enter fullscreen silently
        if (fullscreenExitCount.current === 1) {
          console.log("First fullscreen exit - allowed");

          // Re-enter fullscreen without warning
          setTimeout(() => {
            enterFullscreen();
          }, 500);

          return;
        }

        // ❌ Second time → warning + auto submit
        if (fullscreenExitCount.current >= 2) {
          swal({
            title: "Violation Detected",
            text: "You exited fullscreen. The test will now be submitted.",
            icon: "warning",
            button: "OK",
            closeOnClickOutside: false,
            closeOnEsc: false
          }).then(() => {
            submitAssignment("violation");
          });
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [submitAssignment]);

  /* ---------- TAB SWITCH / FOCUS LOSS AUTO SUBMIT ---------- */
  useEffect(() => {
    const handleBlur = () => {
      swal({
        title: "Focus Lost",
        text: "Switching applications or minimizing is not allowed. The test will now be submitted.",
        icon: "warning",
        button: "OK",
        closeOnClickOutside: false,
        closeOnEsc: false
      }).then(() => {
        submitAssignment("violation");
      });
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        swal({
          title: "Tab Switch Detected",
          text: "Switching tabs is not allowed. The test will now be submitted.",
          icon: "warning",
          button: "OK",
          closeOnClickOutside: false,
          closeOnEsc: false
        }).then(() => {
          submitAssignment("violation");
        });
      }
    };

    window.addEventListener("blur", handleBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [submitAssignment]);

  /* ---------- FETCH ASSIGNMENT ---------- */
  useEffect(() => {
    async function fetchAssignment() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/assignments/${assignmentId}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`
            }
          }
        );

        if (res.status === 401 || res.status === 403) {
          logout();
          navigate("/login");
          return;
        }

        const data = await res.json();
        setAssignment(data);

        const key = `assignment_completed_questions_${assignmentId}_${studentId}`;
        setCompletedQuestions(
          JSON.parse(localStorage.getItem(key)) || []
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchAssignment();
  }, [assignmentId, studentId, navigate]);

  const goFullscreenAndNavigate = async (path) => {
    await enterFullscreen();
    navigate(path);
  };

  if (loading) {
    return <div className="assignment-loader">Loading assignment...</div>;
  }

  return (
    <div className="assignment-solve-page" ref={containerRef}>
      <div className="assignment-header">
        <div>
          <h2>{assignment.name}</h2>
          <p>{assignment.description}</p>
        </div>

        <div className="assignment-header-right">
          <AssignmentTimer
            assignmentId={assignmentId}
            onTimeUp={submitAssignment}
          />

          <span className="assignment-meta">
            Due: {new Date(assignment.dueDate).toLocaleDateString()}
          </span>

          <button
            className="end-test-btn"
            onClick={() => setShowModal(true)}
          >
            End Test
          </button>
        </div>
      </div>

      <div className="assignment-body">
        <div className="questions-section">
          <h3>Questions</h3>

          <div className="question-list">
            {assignment.questions.map((q, index) => {
              const completed = completedQuestions.includes(q._id);

              return (
                <div
                  key={q._id}
                  className={`question-card ${
                    completed ? "completed" : ""
                  }`}
                >
                  <span className="question-index">
                    Question {index + 1}
                  </span>

                  <div className="question-title">{q.title}</div>

                  <button
                    className="solve-btn"
                    disabled={completed}
                    onClick={() =>
                      goFullscreenAndNavigate(
                        `/assignments/solve/${assignmentId}/question/${q._id}`
                      )
                    }
                  >
                    {completed ? "Completed ✓" : "Solve →"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>End Test</h3>
            <p>Are you sure you want to end the test? This will submit your assignment.</p>
            <div className="modal-buttons">
              <button
                className="cancel-btn"
                onClick={() => setShowModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={() => {
                  setShowModal(false);
                  submitAssignment("manual");
                }}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "End Test"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
